<template>
  <div class="checkout-container">
    <div class="stepper-progress-wrapper">
      <stepper-progress :current-step="1"></stepper-progress>
    </div>

    <div class="booking-wrapper">
      <transition name="fade">
        <div class="sidebar-overlay" v-if="sidebarOpen" @click="closeSidebar"></div>
      </transition>

      <transition name="slide">
        <div class="sidebar-wrapper" v-show="sidebarOpen">
          <div class="sidebar-header">
            <h3 class="sidebar-title">
              <v-icon small class="mr-1">mdi-cart</v-icon>
              Your Booking Cart
              <span v-if="cartItemCount > 0" class="sidebar-badge">{{ cartItemCount }}</span>
            </h3>
            <v-btn icon small @click="closeSidebar" class="close-sidebar-btn">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <side-bar ref="sidebar"></side-bar>
        </div>
      </transition>

      <div class="main-content">
        <v-card flat class="stepper-card">
          <div class="stepper-header">
            <div
              v-for="(step, index) in steps"
              :key="index"
              class="step-item"
              :class="{
                active: currentStep === index,
                completed: currentStep > index,
                clickable: currentStep > index
              }"
              @click="currentStep > index ? goToStep(index) : null"
            >
              <div class="step-circle">
                <span v-if="currentStep > index" class="step-check">✓</span>
                <span v-else class="step-number">{{ index + 1 }}</span>
              </div>
              <div class="step-label">{{ step.label }}</div>
              <div v-if="index < steps.length - 1" class="step-line"></div>
            </div>
          </div>

          <div class="stepper-content">
            <!-- STEP 0: DATES -->
            <div v-show="currentStep === 0" class="step-panel">
              <div class="step-actions-top">
                <v-btn
                  color="#74a533"
                  dark
                  @click="nextStep"
                  :disabled="!isDateSelectionValid"
                  class="step-btn"
                >
                  Continue to Guests
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
              <div class="step-body">
                <div class="step-header-section">
                  <h2 class="step-title">
                    <v-icon class="step-icon" color="#74a533">mdi-calendar-range</v-icon>
                    Select Your Dates
                  </h2>
                </div>
                <v-card flat class="step-card">
                  <v-container>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-menu
                          v-model="checkInMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="checkInDate"
                              label="Check-in Date"
                              prepend-icon="mdi-calendar"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              outlined
                              dense
                              class="date-picker-field"
                              :error-messages="checkInError"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="checkInDate"
                            :min="today"
                            :max="maxDate"
                            @input="onCheckInSelect"
                            locale="en"
                            color="#74a533"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-menu
                          v-model="checkOutMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="checkOutDate"
                              label="Check-out Date"
                              prepend-icon="mdi-calendar"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              outlined
                              dense
                              class="date-picker-field"
                              :error-messages="checkOutError"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="checkOutDate"
                            :min="minCheckOutDate"
                            :max="maxDate"
                            @input="onCheckOutSelect"
                            locale="en"
                            color="#74a533"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                    <v-row v-if="dateValidationMessage">
                      <v-col cols="12">
                        <v-alert type="error" dense text>{{ dateValidationMessage }}</v-alert>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </div>
            </div>

            <!-- STEP 1: GUESTS -->
            <div v-show="currentStep === 1" class="step-panel">
              <div class="step-actions-top">
                <v-btn text @click="prevStep" class="step-btn-back">
                  <v-icon left>mdi-arrow-left</v-icon> Back
                </v-btn>
                <v-btn
                  color="#74a533"
                  dark
                  @click="nextStep"
                  :disabled="!isGuestSelectionValid"
                  class="step-btn"
                >
                  Continue to Vehicles
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
              <div class="step-body">
                <div class="step-header-section">
                  <h2 class="step-title">
                    <v-icon class="step-icon" color="#74a533">mdi-account-group</v-icon>
                    Guest Information
                  </h2>
                </div>
                <v-card flat class="step-card">
                  <v-container>
                    <v-row>
                      <v-col cols="12">
                        <guests ref="guestsComponent" @guests-auto-updated="handleGuestsUpdated"></guests>
                      </v-col>
                    </v-row>
                    <v-row v-if="guestValidationMessage">
                      <v-col cols="12">
                        <v-alert type="error" dense text>{{ guestValidationMessage }}</v-alert>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <div class="guest-summary">
                          <span class="guest-total">Total Guests: {{ totalGuests }}</span>
                          <span v-if="totalGuests > 0" class="guest-badge">
                            {{ totalGuests }} guest{{ totalGuests > 1 ? 's' : '' }}
                          </span>
                        </div>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </div>
            </div>

            <!-- STEP 2: VEHICLES -->
            <div v-show="currentStep === 2" class="step-panel">
              <div class="step-actions-top">
                <v-btn text @click="prevStep" class="step-btn-back">
                  <v-icon left>mdi-arrow-left</v-icon> Back
                </v-btn>
                <v-btn
                  color="#74a533"
                  dark
                  @click="nextStep"
                  :disabled="!isVehicleConditionMet"
                  class="step-btn"
                >
                  Continue to Activities
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
              <div class="step-body">
                <div class="step-header-section">
                  <h2 class="step-title">
                    <v-icon class="step-icon" color="#74a533">mdi-car</v-icon>
                    Vehicle Details
                  </h2>
                </div>
                <v-card flat class="step-card">
                  <v-container>
                    <v-row>
                      <v-col cols="12">
                        <vehicle ref="vehicleComponent" @vehicles-auto-updated="handleVehiclesUpdated"></vehicle>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <div class="vehicle-summary">
                          <span class="vehicle-total">Total Vehicles: {{ totalVehicles }}</span>
                          <span v-if="vehicleFeeSeparate" class="vehicle-badge">Fee booked separately</span>
                        </div>
                      </v-col>
                    </v-row>
                    <v-row v-if="!isVehicleConditionMet">
                      <v-col cols="12">
                        <v-alert type="warning" dense text>
                          Add a vehicle or mark the fee as booked separately.
                        </v-alert>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </div>
            </div>

            <!-- STEP 3: ACTIVITIES -->
            <div v-show="currentStep === 3" class="step-panel">
              <div class="step-actions-top">
                <v-btn text @click="prevStep" class="step-btn-back">
                  <v-icon left>mdi-arrow-left</v-icon> Back
                </v-btn>
                <v-btn text @click="nextStep" class="step-btn-skip">Skip</v-btn>
                <v-btn color="#74a533" dark @click="nextStep" class="step-btn">
                  Continue to Camping & Accommodation
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
              <div class="step-body">
                <div class="step-header-section">
                  <h2 class="step-title">
                    <v-icon class="step-icon" color="#74a533">mdi-hiking</v-icon>
                    Activities
                  </h2>
                </div>
                <v-card flat class="step-card">
                  <activities ref="activitiesComponent"></activities>
                </v-card>
              </div>
            </div>

            <!-- STEP 4: CAMPING & ACCOMMODATION (Combined) -->
            <div v-show="currentStep === 4" class="step-panel">
              <div class="step-actions-top">
                <v-btn text @click="prevStep" class="step-btn-back">
                  <v-icon left>mdi-arrow-left</v-icon> Back
                </v-btn>
                <v-btn text @click="openCartSidebar" class="step-btn-skip">Skip</v-btn>
                <v-btn color="#74a533" dark @click="openCartSidebar" class="step-btn">
                  View Cart & Summary
                  <v-icon right>mdi-cart-check</v-icon>
                </v-btn>
              </div>
              <div class="step-body">
                <div class="step-header-section">
                  <h2 class="step-title">
                    <v-icon class="step-icon" color="#74a533">mdi-tent</v-icon>
                    Accommodation & Camping
                  </h2>
                </div>
                <v-card flat class="step-card">
                  <camp-sites ref="campingComponent"></camp-sites>
                </v-card>
              </div>
            </div>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import CampSites from '../../Public/Repeated/CampSites'
import Activities from '../../Public/Repeated/Activities'
import SideBar from '../../Public/Repeated/SideBar'
import Guests from '../../Public/Repeated/Guests'
import Vehicle from '../../Public/Repeated/Vehicle'
import StepperProgress from '../Guests/StepperProgress.vue'

export default {
  name: 'NewGuestBooking',
  components: {
    Activities,
    CampSites,
    SideBar,
    Guests,
    Vehicle,
    StepperProgress
  },
  data() {
    return {
      currentStep: 0,
      // REMOVED Accommodation step
      steps: [
        { label: 'Dates' },
        { label: 'Guests' },
        { label: 'Vehicles' },
        { label: 'Activities' },
        { label: 'Camping' }
      ],
      checkInDate: null,
      checkOutDate: null,
      checkInMenu: false,
      checkOutMenu: false,
      today: new Date().toISOString().slice(0, 10),
      maxDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
        .toISOString()
        .slice(0, 10),
      checkInError: '',
      checkOutError: '',
      dateValidationMessage: '',
      totalGuests: 0,
      totalVehicles: 0,
      vehicleFeeSeparate: false,
      guestValidationMessage: '',
      sidebarOpen: false,
      cartItemCount: 0,
      cartInterval: null,
      unsubscribe: null
    }
  },
  computed: {
    minCheckOutDate() {
      return this.checkInDate || this.today
    },
    isDateSelectionValid() {
      return this.checkInDate && this.checkOutDate && this.checkOutDate >= this.checkInDate
    },
    isUserLoggedIn() {
      const name = localStorage.getItem('user_name')
      return name && name !== 'null' && name !== ''
    },
    globalGuests() {
      const bg = this.$store.state.GlobalBookingStore
      if (bg && bg.booking_guests) return bg.booking_guests
      return { adults: 0, children: 0, total: 0 }
    },
    isGuestSelectionValid() {
      if (this.totalGuests > 0) return true
      if (this.globalGuests.total > 0) return true
      try {
        const g = JSON.parse(localStorage.getItem('guests') || '{"adults":0,"children":0}')
        const total = g.total !== undefined ? g.total : (g.adults || 0) + (g.children || 0)
        return total > 0
      } catch (e) {
        return false
      }
    },
    isVehicleConditionMet() {
      return this.totalVehicles > 0 || this.vehicleFeeSeparate
    }
  },
  watch: {
    totalGuests(newVal) {
      this.guestValidationMessage = newVal > 0 ? '' : 'Please add at least one guest'
    },
    checkInDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.validateDates()
        this.saveDateSelection()
      }
    },
    checkOutDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.validateDates()
        this.saveDateSelection()
      }
    }
  },
  created() {
    this.fetchInitialData()
    this.initializeStorage()
    this.loadStateFromStorage()
    this.loadDatesFromGlobalStore()
    this.commitStoredDataToVuex()
    this.updateCartCount()

    this.cartInterval = setInterval(() => {
      this.updateCartCount()
    }, 2500)

    if (this.$store && this.$store.subscribe) {
      this.unsubscribe = this.$store.subscribe((mutation) => {
        if (!mutation.type) return
        const t = mutation.type
        if (
          t.includes('ACTIVITIES') ||
          t.includes('FACILITIES') ||
          t.includes('VEHICLES') ||
          t.includes('CONSERVATION') ||
          t.includes('VEHICLE_SEPARATE')
        ) {
          this.updateCartCount()
        }
      })
    }

    this.$root.$on('open-sidebar', this.openSidebarFromEvent)
    this.$root.$on('cart-updated', this.handleCartUpdated)
    window.addEventListener('storage', this.handleStorageChange)
  },
  mounted() {
    this.$nextTick(() => {
      this.loadStateFromStorage()
      this.loadDatesFromGlobalStore()
      this.commitStoredDataToVuex()
    })
  },
  beforeDestroy() {
    if (this.cartInterval) clearInterval(this.cartInterval)
    if (this.unsubscribe) this.unsubscribe()
    this.$root.$off('open-sidebar', this.openSidebarFromEvent)
    this.$root.$off('cart-updated', this.handleCartUpdated)
    window.removeEventListener('storage', this.handleStorageChange)
  },
  methods: {
    fetchInitialData() {
      const actions = [
        'get_all_vehicle_fees',
        'get_all_conservation_fees',
        'get_active_activities',
        'get_active_accommodation',
        'get_active_camping'
      ]
      actions.forEach(a => {
        if (this.$store._actions && this.$store._actions[a]) {
          this.$store.dispatch(a).catch(err => console.warn(`${a} fetch error:`, err))
        }
      })
    },

    initializeStorage() {
      const isLoggedIn = this.isUserLoggedIn

      if (!localStorage.getItem('user_data')) {
        const userPublicId = localStorage.getItem('user_public_id') || ''

        const userData = {
          currency: 'KES',
          type: 'GB601X10',
          user_type: isLoggedIn ? 'Registered' : 'Guest',
          first_name: isLoggedIn ? (localStorage.getItem('user_first_name') || '') : '',
          last_name: isLoggedIn ? (localStorage.getItem('user_last_name') || '') : '',
          email: isLoggedIn ? (localStorage.getItem('user_email') || '') : '',
          phone: isLoggedIn ? (localStorage.getItem('user_phone') || '') : '',
          phone_number: isLoggedIn ? (localStorage.getItem('user_phone') || '') : '',
          country: isLoggedIn ? (localStorage.getItem('user_country') || '') : '',
          country_code: '',
          city: isLoggedIn ? (localStorage.getItem('user_city') || '') : '',
          destination: '',
          check_in: this.checkInDate || '',
          check_out: this.checkOutDate || '',
          session_id: localStorage.getItem('lkjhgfdsa'),
          public_id: userPublicId,
          status: 'active'
        }
        localStorage.setItem('user_data', JSON.stringify(userData))
      }

      if (!localStorage.getItem('vehicle_data')) localStorage.setItem('vehicle_data', JSON.stringify([]))
      if (!localStorage.getItem('conservation_data')) localStorage.setItem('conservation_data', JSON.stringify([]))
      if (!localStorage.getItem('facility_data')) localStorage.setItem('facility_data', JSON.stringify([]))
      if (!localStorage.getItem('activity_data')) localStorage.setItem('activity_data', JSON.stringify([]))
      if (!localStorage.getItem('vehicle_separate')) localStorage.setItem('vehicle_separate', JSON.stringify(false))
      if (!localStorage.getItem('guests')) localStorage.setItem('guests', JSON.stringify({ adults: 0, children: 0 }))
    },

    loadStateFromStorage() {
      try {
        const guestsData = JSON.parse(localStorage.getItem('guests') || '{"adults":0,"children":0}')
        this.totalGuests =
          guestsData.total !== undefined
            ? guestsData.total
            : (guestsData.adults || 0) + (guestsData.children || 0)
      } catch (e) {
        this.totalGuests = 0
      }

      try {
        const separate = localStorage.getItem('vehicle_separate')
        this.vehicleFeeSeparate = separate !== null ? JSON.parse(separate) : false
      } catch (e) {
        this.vehicleFeeSeparate = false
      }

      try {
        this.totalVehicles = JSON.parse(localStorage.getItem('vehicle_data') || '[]').length
      } catch (e) {
        this.totalVehicles = 0
      }

      const bg = this.$store.state.GlobalBookingStore
      if (bg) {
        if (bg.booking_guests && bg.booking_guests.total > this.totalGuests) {
          this.totalGuests = bg.booking_guests.total
        }
        if (bg.booking_vehicles) {
          if (bg.booking_vehicles.total !== undefined) this.totalVehicles = bg.booking_vehicles.total
          if (bg.booking_vehicles.vehicleFeeSeparate !== undefined) {
            this.vehicleFeeSeparate = bg.booking_vehicles.vehicleFeeSeparate
          }
        }
      }

      this.guestValidationMessage = this.totalGuests > 0 ? '' : 'Please add at least one guest'
    },

    commitStoredDataToVuex() {
      const safeCommit = (type, payload) => {
        if (this.$store._mutations && this.$store._mutations[type]) {
          this.$store.commit(type, payload)
        }
      }
      try { safeCommit('CHOSEN_ACTIVITIES', JSON.parse(localStorage.getItem('activity_data') || '[]')) } catch (e) {}
      try { safeCommit('CHOSEN_FACILITIES', JSON.parse(localStorage.getItem('facility_data') || '[]')) } catch (e) {}
      try { safeCommit('CHOSEN_CONSERVATION_FEES', JSON.parse(localStorage.getItem('conservation_data') || '[]')) } catch (e) {}
      try { safeCommit('CHOSEN_VEHICLES', JSON.parse(localStorage.getItem('vehicle_data') || '[]')) } catch (e) {}
      try {
        const sep = localStorage.getItem('vehicle_separate')
        if (sep !== null) safeCommit('SET_VEHICLE_SEPARATE', JSON.parse(sep))
      } catch (e) {}
    },

    loadDatesFromGlobalStore() {
      const bg = this.$store.state.GlobalBookingStore
      if (bg && bg.booking_dates) {
        const gd = bg.booking_dates
        let changed = false
        if (gd.check_in && gd.check_in !== this.checkInDate) {
          this.checkInDate = gd.check_in
          changed = true
        }
        if (gd.check_out && gd.check_out !== this.checkOutDate) {
          this.checkOutDate = gd.check_out
          changed = true
        }
        if (changed) {
          this.validateDates()
          this.saveDateSelection()
        }
      }
      try {
        const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
        if (userData.check_in && !this.checkInDate) this.checkInDate = userData.check_in
        if (userData.check_out && !this.checkOutDate) this.checkOutDate = userData.check_out
        if (userData.check_in || userData.check_out) {
          this.validateDates()
          this.saveDateSelection()
        }
      } catch (e) {}
    },

    nextStep() {
      if (this.currentStep === 2 && !this.isVehicleConditionMet) {
        alert('Please add at least one vehicle or check "Vehicle fee already booked separately" before proceeding.')
        return
      }
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++
      }
    },
    prevStep() {
      if (this.currentStep > 0) this.currentStep--
    },
    goToStep(index) {
      if (index <= this.currentStep) this.currentStep = index
    },

    onCheckInSelect(date) {
      this.checkInDate = date
      this.checkInMenu = false
      if (this.checkOutDate && this.checkOutDate < this.checkInDate) {
        this.checkOutDate = null
      }
    },
    onCheckOutSelect(date) {
      this.checkOutDate = date
      this.checkOutMenu = false
    },
    validateDates() {
      this.checkInError = ''
      this.checkOutError = ''
      this.dateValidationMessage = ''
      if (this.checkInDate && this.checkOutDate) {
        if (this.checkOutDate < this.checkInDate) {
          this.dateValidationMessage = 'Check-out date must be on or after check-in date'
        } else {
          const diffDays = Math.ceil(
            (new Date(this.checkOutDate) - new Date(this.checkInDate)) / (1000 * 60 * 60 * 24)
          )
          if (diffDays > 365) {
            this.dateValidationMessage = 'Booking duration cannot exceed 365 days'
          }
        }
      }
    },
    saveDateSelection() {
      if (!this.isDateSelectionValid) return
      const diffDays = Math.ceil(
        (new Date(this.checkOutDate) - new Date(this.checkInDate)) / (1000 * 60 * 60 * 24)
      )
      const dates = { check_in: this.checkInDate, check_out: this.checkOutDate, days: diffDays }
      if (this.$store.dispatch && this.$store.state.GlobalBookingStore) {
        this.$store.dispatch('GlobalBookingStore/updateBookingDates', dates)
      }
      try {
        const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
        userData.check_in = this.checkInDate
        userData.check_out = this.checkOutDate
        localStorage.setItem('user_data', JSON.stringify(userData))
      } catch (e) {}
    },

    handleGuestsUpdated(guestsData) {
      this.totalGuests = guestsData.total || 0
      this.updateCartCount()
      this.guestValidationMessage = this.totalGuests > 0 ? '' : 'Please add at least one guest'
    },
    handleVehiclesUpdated(vehiclesData) {
      this.totalVehicles = vehiclesData.total || 0
      this.vehicleFeeSeparate = vehiclesData.vehicleFeeSeparate || false
      this.updateCartCount()
    },

    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen
      document.body.style.overflow = this.sidebarOpen ? 'hidden' : ''
    },
    closeSidebar() {
      this.sidebarOpen = false
      document.body.style.overflow = ''
    },
    openSidebarFromEvent() {
      this.sidebarOpen = true
      document.body.style.overflow = 'hidden'
      this.updateCartCount()
    },
    openCartSidebar() {
      this.sidebarOpen = true
      document.body.style.overflow = 'hidden'
      this.updateCartCount()
    },

    updateCartCount() {
      const facilities = JSON.parse(localStorage.getItem('facility_data') || '[]')
      const vehicles = JSON.parse(localStorage.getItem('vehicle_data') || '[]')
      const conservation = JSON.parse(localStorage.getItem('conservation_data') || '[]')
      const activities = JSON.parse(localStorage.getItem('activity_data') || '[]')

      let total = 0
      if (conservation.length > 0) total += 1
      if (vehicles.length > 0) total += 1

      const uniqueFacilities = new Set()
      facilities.forEach(f => uniqueFacilities.add(f.id || f.public_id || f.facility_id || f.name))
      total += uniqueFacilities.size

      const uniqueActivities = new Set()
      activities.forEach(a => {
        uniqueActivities.add(`${a.name || a.activity_name}_${a.date}_${a.time || a.start_time}`)
      })
      total += uniqueActivities.size

      this.cartItemCount = total
    },
    handleCartUpdated() {
      this.updateCartCount()
    },

    handleStorageChange(event) {
      const keys = [
        'activity_data',
        'facility_data',
        'conservation_data',
        'vehicle_data',
        'vehicle_separate',
        'guests'
      ]
      if (keys.includes(event.key)) {
        this.loadStateFromStorage()
        this.commitStoredDataToVuex()
        this.updateCartCount()
      }
    }
  }
}
</script>

<style scoped>

.checkout-container {
  --brand: #74a533;
  --brand-dark: #5a8a2a;
  --ink-900: #1a2233;
  --ink-700: #37424f;
  --ink-500: #6b7684;
  --line: #e6eaee;
  --bg-soft: #f7f9fb;
  --radius-md: 12px;
  --radius-lg: 16px;

    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
    position: relative;
}

.stepper-progress-wrapper {
  background: transparent;
}
.stepper-progress-wrapper :deep(.stepper-progress),
.stepper-progress-wrapper :deep(.stepper-container) {
  background: transparent !important;
  box-shadow: none !important;
  border-bottom: none !important;
}

.booking-wrapper {
  display: flex;
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  gap: 24px;
  position: relative;
}

.main-content {
  flex: 1;
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05), 0 6px 20px rgba(16, 24, 40, 0.05);
  padding: 24px;
  overflow: hidden;
}

.stepper-card {
  border-radius: var(--radius-md);
  background: #fff;
}

.stepper-header {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 4px 20px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 20px;
  overflow-x: auto;
  scrollbar-width: none;
}
.stepper-header::-webkit-scrollbar { display: none; }

.step-item {
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
  position: relative;
}
.step-item.clickable { cursor: pointer; }

.step-circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #eef1f4;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: var(--ink-500);
  font-size: 13px;
  flex-shrink: 0;
  transition: all 0.25s ease;
  border: 2px solid transparent;
}
.step-item.active .step-circle {
  background: var(--brand);
  color: #fff;
  box-shadow: 0 4px 12px rgba(116, 165, 51, 0.35);
  border-color: #fff;
}
.step-item.completed .step-circle {
  background: var(--brand);
  color: #fff;
}
.step-check { font-size: 15px; }

.step-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-500);
  margin-left: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.step-item.active .step-label,
.step-item.completed .step-label {
  color: var(--brand-dark);
  font-weight: 600;
}

.step-line {
  flex: 1;
  height: 2px;
  background: var(--line);
  margin: 0 10px;
  min-width: 12px;
  border-radius: 2px;
}
.step-item.completed .step-line { background: var(--brand); }

.step-panel {
  display: flex;
  flex-direction: column;
  min-height: 360px;
  background: #fff;
  border-radius: var(--radius-md);
}

.step-actions-top {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 8px 0 14px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.step-body { flex: 1; }

.step-card {
  padding: 20px;
  background: var(--bg-soft);
  border-radius: var(--radius-md);
  border: 1px solid var(--line);
}

.step-header-section {
  margin-bottom: 20px;
  text-align: center;
}

.step-title {
  font-size: clamp(1.1rem, 2vw, 1.5rem);
  font-weight: 700;
  color: var(--ink-900);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0;
  letter-spacing: -0.01em;
}

.step-icon { font-size: 26px !important; }

.step-btn {
  min-width: 160px;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  border-radius: 10px !important;
  padding: 8px 20px !important;
}

.step-btn-back,
.step-btn-skip {
  text-transform: none !important;
  font-weight: 500 !important;
  border-radius: 10px !important;
}
.step-btn-back { color: var(--ink-700) !important; }
.step-btn-back:hover { color: var(--brand) !important; }
.step-btn-skip { color: var(--ink-500) !important; }
.step-btn-skip:hover { color: var(--brand) !important; }

.date-picker-field >>> .v-input__prepend-outer { color: var(--brand); }

.guest-summary,
.vehicle-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0 0;
  border-top: 1px solid var(--line);
  margin-top: 10px;
  flex-wrap: wrap;
}

.guest-total,
.vehicle-total {
  font-weight: 600;
  font-size: 1rem;
  color: var(--ink-900);
}

.guest-badge,
.vehicle-badge {
  background: var(--brand);
  color: #fff;
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 500;
}

.sidebar-wrapper {
  position: fixed;
  top: 0;
  right: 0;
  width: 380px;
  max-width: 92vw;
  height: 100vh;
  background: #fff;
  box-shadow: -6px 0 28px rgba(0, 0, 0, 0.14);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.slide-enter-active,
.slide-leave-active { transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.slide-enter,
.slide-leave-to { transform: translateX(100%); }

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--brand);
  color: #fff;
  position: sticky;
  top: 0;
  z-index: 10;
}

.sidebar-title {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.sidebar-badge {
  background: #ff5252;
  color: #fff;
  border-radius: 999px;
  min-width: 22px;
  height: 22px;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  margin-left: 8px;
}

.close-sidebar-btn {
  color: #fff !important;
  background: rgba(255, 255, 255, 0.18) !important;
  border-radius: 50% !important;
}

.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 33, 0.55);
  z-index: 999;
  backdrop-filter: blur(1px);
}

.fade-enter-active,
.fade-leave-active { transition: opacity 0.25s ease; }
.fade-enter,
.fade-leave-to { opacity: 0; }

@media (min-width: 1600px) {
  .booking-wrapper { padding: 32px; }
  .main-content { padding: 32px; }
}

@media (max-width: 960px) {
  .booking-wrapper {
    flex-direction: column;
    padding: 16px;
    gap: 16px;
  }
  .main-content { padding: 18px; }
  .step-actions-top { gap: 8px; }
  .step-panel { min-height: 320px; }
}

@media (max-width: 600px) {
  .booking-wrapper { padding: 0; }
  .main-content {
    padding: 10px 8px;
    border-radius: 0;
    box-shadow: none;
  }

  .stepper-header { padding: 8px 0 14px; gap: 2px; }
  .step-label { font-size: 10px; margin-left: 4px; max-width: 52px; }
  .step-circle { width: 28px; height: 28px; font-size: 11px; }
  .step-line { margin: 0 4px; min-width: 4px; }

  .step-actions-top {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 8px;
    padding: 6px 4px 12px;
    margin-bottom: 12px;
  }
  .step-btn,
  .step-btn-back,
  .step-btn-skip {
    width: 100% !important;
    min-width: 100% !important;
    justify-content: center;
  }

  .step-panel { min-height: 280px; }

  /* Flatten the wrapper so child components aren't boxed-in */
  .step-card {
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 0;
  }

  .step-header-section {
    margin: 0 4px 10px;
    text-align: left;
  }
  .step-title {
    justify-content: flex-start;
    font-size: 1.05rem;
  }
  .step-icon { font-size: 22px !important; }

  /* Neutralize common nested card/container paddings inside children */
  .step-card :deep(.v-card) {
    box-shadow: none !important;
    border-radius: 10px !important;
  }
  .step-card :deep(.v-container),
  .step-card :deep(.container) {
    padding: 8px !important;
  }
  .step-card :deep(.v-card__text) {
    padding: 10px !important;
  }
  .step-card :deep(.v-row) {
    margin: 0 !important;
  }
  .step-card :deep(.v-col) {
    padding: 6px !important;
  }

  .sidebar-wrapper {
    width: 100%;
    max-width: 100%;
    height: 85vh;
    top: auto;
    bottom: 0;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
  }
  .slide-enter, .slide-leave-to { transform: translateY(100%); }
}

@media (max-width: 380px) {
  .main-content { padding: 10px; }
  .step-label { display: none; }
  .step-circle { width: 26px; height: 26px; font-size: 10px; }
  .step-item { min-width: 0; }
  .step-line { margin: 0 3px; min-width: 3px; }
  .step-title { font-size: 1rem; gap: 6px; }
  .step-icon { font-size: 20px !important; }
}
</style>